import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy
} from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { IonButton, IonIcon } from "@ionic/angular";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Plant } from "src/model/class/general classes/plant";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { selectedPlant } from "src/state/selectors/plant.selectors";

@Component({
    selector: "app-error-content",
    templateUrl: "./error-content.component.html",
    styleUrls: ["./error-content.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    // Standalone: the pages of the shared-components submodule list it in their own imports
    imports: [IonIcon, IonButton, TranslatePipe],
})

/**
 *
 *  This component manage error generic visualization in page.
 *  It accept an optional parameter in input called "text"
 *  If no text parameter is passed to component, the default text is "Ops qualcosa è andato storto"
 *
 *
 */
export class ErrorContentComponent implements OnInit, OnDestroy, iTranslation, iEvent {
  private _logContext: string = "Error-content.component";

  private _networkServiceSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;

  private _selectCurrentPlantSub: Subscription = null;
  private _currentPlant: Plant = null;
  private _restrictHeartBit: any = null;

  /**
   * This "twin" timer is used to keep track of how much time has passed from the previous 
    time-out of "_restrictHeartBit" and, therefore, how long till its next time-out occurs. 
    NOTE: This timer is used ONLY for change the visible lable displaying the count-down timer 
   */
  private _twinTimer: any = null;

  //NOTE: Interval of ciclyc "automatic retry" should be only slighlty longer than multiples of the global "heartBeat polling" used
  //in NEtwork.service to check for connection to server, since this component use it to decide if is useful to
  //retry the reload of data (Connection present) or not (Connecion absent)
  private _intervalRetryReload: number = 12000;


  private _lblError: string = "";
  private _lblLocalisedContentCountdown: string = "";
  private _lblReloadingInProgress: string = "";
  private _lblNoConnectionForReloading: string = "";

  public _eventSubscriptions: Array<Subscription> = null;

  public realtText: string = "";
  public countdownText: string = "";
  public buttonColor: string = "primary";

  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public connectionType = ConnectionType;

  private _pollerNumber: number = 0;

  @Input() text: string;
  @Input() showButtonReload: boolean;

  /**
   * Name of the page/component hosting the error, written in the logs. Passed by the pages of the
   * shared-components submodule, as in BIGStudio3
   */
  @Input() debugContainerClass: string = "";

  /**
   * HTTP error code of the failed data retrieval, passed by the pages of the shared-components submodule.
   * In BIGStudio3 it selects the error case; here it is only written in the logs, the retry policy stays
   * the one of Digital Place
   */
  @Input() errorCodeFromDataRetrieval: number;

  @Output() needReloadData = new EventEmitter<boolean>();

  constructor(
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _store: Store,
    private _eventService: EventService,
    private _loggerService: LoggerService
  ) { }

  ngOnInit() {
    this._pollerNumber = 0;

    this.subscribeCurrentPlant();

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();

    this.subscribeEvents();

    if (!this.text) {
      this.realtText = this._lblError;
    } else {
      this.realtText = this.text;
    }

    if (this.showButtonReload === null || this.showButtonReload === undefined) {
      this.showButtonReload = true;
    }

    this.showButtonReload =
      "TRUE" === this.showButtonReload.toString().toUpperCase();

    this.handleAutomaticRetry();
  }

  ngOnDestroy() {
    if (this._restrictHeartBit) {
      clearInterval(this._restrictHeartBit);
    }
    this._restrictHeartBit = null;

    if (this._twinTimer) {
      clearInterval(this._twinTimer);
    }
    this._twinTimer = null;

    this._currentPlant = null;
    this._translateSubscription.unsubscribe();
    this._networkServiceSubscription.unsubscribe();
    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(
        ["dictionary.somethingWrong",
          "dictionary.countDownToRetry",
          "dictionary.reloadingDataInProgress",
          "dictionary.noConnectionForReloading"
        ])
      .subscribe((translation) => {
        this._lblError = translation["dictionary.somethingWrong"];
        this._lblLocalisedContentCountdown = translation["dictionary.countDownToRetry"];
        this._lblReloadingInProgress = translation["dictionary.reloadingDataInProgress"];
        this._lblNoConnectionForReloading = translation["dictionary.noConnectionForReloading"]
      });

  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Fired when Reload button is clicked
   */
  public onReloadData() {
    clearInterval(this._restrictHeartBit);
    this._restrictHeartBit = null;

    clearInterval(this._twinTimer);
    this._twinTimer = null;

    this._pollerNumber = this._pollerNumber - 1;

    this.handleRetryForSpecificMode();

  }

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.buttonColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }




  /**
   * This method handle the "automatic retry" policy applied if some other component/page of the app has suffered from a data-retrieve error;
   * this is usually linked to an error in APis execution which caused the calls failure. 
   * This method follows the most generic approach possible, re-executing periodically all the data-retrieving procedures that resulted in the error.
   * 
   */
  private handleAutomaticRetry(): void {

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleAutomaticRetry()",
      "ACTIVATE ( Test fallimento ) - CLASS: " + (this.debugContainerClass ?? "") +
      " | errorCodeFromDataRetrieval: " + this.errorCodeFromDataRetrieval
    );

    this._pollerNumber = this._pollerNumber + 1;

    /*This "twin" timer is used to keep track of how much time has passed from the previous
    time-out of "_restrictHeartBit" and, therefore, how long till its next time-out occurs. 
    NOTE: This timer is used ONLY for change the visible lable displaying the count-down timer 
    */
    let countDownSeconds: number = this._intervalRetryReload / 1000;
    this.countdownText = this._lblLocalisedContentCountdown.replace("XX", countDownSeconds.toString());
    countDownSeconds = countDownSeconds - 1;

    this._twinTimer = setInterval(async () => {

      const countDownDigitStr: string = countDownSeconds.toString();

      if (this._currentPlant?.operativeMode == BIGDeviceOperationMode.STANDARD && this.currentConnectionType == ConnectionType.NONE) {
        this.countdownText = this._lblNoConnectionForReloading;
      } else {
        this.countdownText = this._lblLocalisedContentCountdown.replace("XX", countDownDigitStr);
      }

      countDownSeconds = countDownSeconds - 1;

      if (countDownSeconds < 0) {
        countDownSeconds = this._intervalRetryReload;
      }


    }, 1000);


    /*
    Main timer; it handldes the automatic retry, ciclically asking the father page/component to re-execute all the
    data-retrieving operations.
    */
    this._restrictHeartBit = setInterval(async () => {
      this.countdownText = this._lblReloadingInProgress;
      await this.handleRetryForSpecificMode();

    }, this._intervalRetryReload);

  }

  private subscribeCurrentPlant(): void {
    this._selectCurrentPlantSub = this._store
      .select(selectedPlant)
      .pipe(take(1))
      .subscribe((currentPlant: Plant) => {
        this._currentPlant = new Plant(currentPlant);
      });
  }

  /**
   *   This method  checks if the server has become reachable by co-opting the general "heartbit" 
     * (in STANDARD_MODE) or by directly executing a puntual "test" call (in STAND_ALONE_MODE or DIGITAL_PLACE_MODE); 
     * in positive case, the component emits the inducing-data-reloading signal (to be handled inside its parent 
     * component/page).
   */
  private async handleRetryForSpecificMode(): Promise<void> {

    switch (this._currentPlant?.operativeMode) {

      //#region Current operative mode: DIGITAL_PLACE_MODE
      case BIGDeviceOperationMode.DIGITAL_PLACE_MODE:

        const DPresultConnection: boolean =
          await this._networkService.handleDigitalPlaceCheckConnectionProcedure();

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleSpecificRetry()",
          "DIGITAL_PLACE_MODE - ResultConnection: " + DPresultConnection
        );

        if (DPresultConnection) {
          this.needReloadData.emit();
          clearInterval(this._twinTimer);
          clearInterval(this._restrictHeartBit);

        }

        break;
      //#endregion

      //#region Current operative mode: STANDARD
      case BIGDeviceOperationMode.STANDARD:

        const resultConnection: ConnectionType = this.currentConnectionType;

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleSpecificRetry()",
          "STANDARD CASE - ( Test fallimento ) in ciclo retry. api/system/test TEST CONNESSIONE 10010. ResultConnection: " + ConnectionType[resultConnection]
        );

        if (resultConnection !== ConnectionType.NONE) {
          this.needReloadData.emit();
          clearInterval(this._twinTimer);
          clearInterval(this._restrictHeartBit);
          this._pollerNumber = this._pollerNumber - 1;
        }

        break;
      //#endregion

      //#region Current operative mode: ACCESS_CONTROL_MODE
      case BIGDeviceOperationMode.ACCESS_CONTROL_MODE:

        const fullAddress: string = this._currentPlant.FullLanAddress;

        const ACMresultConnection: boolean =
          await this._networkService.sendServerHeartbit(
            fullAddress,
            AppSettings.DEFAULT_LAN_TIMEOUT_MS
          );

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleSpecificRetry()",
          "ACCESS_CONTROL_MODE - ResultConnection: " + resultConnection
        );

        if (ACMresultConnection) {
          this.needReloadData.emit();
          clearInterval(this._twinTimer);
          clearInterval(this._restrictHeartBit);
        }

        break;
      //#endregion

      default:
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "handleSpecificRetry()",
          "DEFAULT - currentPlant not initialised!"
        );

        break;

    }

  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentPlantSub) {
      this._selectCurrentPlantSub.unsubscribe();
    }
  }

  //#endregion


  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnErrorContentPageLeaving.subscribe(
        (destroyComponent: boolean) => {
          if (destroyComponent) {
            this.ngOnDestroy();
          }
        }
      )
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion


}
